import React, { useState, useMemo, useRef, useEffect } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { getQuantileStats } from "common/api";
import { createRectanglePath } from "common/utils";

const GAP = 5;
const EXTRA_AXIS_Y_OFFSET = -5;
const WrapperPlot = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    overflow: visible;
  }

  .background {
    fill: transparent;
    stroke: var(--chart-grid-color);
  }
  .axis {
    text {
      fill: var(--Text-Secondary);
      font-size: 9px;

      &.unit-axis {
        font-size: 11px;
      }
    }
    line {
      stroke: var(--Text-Secondary);
    }
  }
  .warning {
    line {
      ${(props) => (!props.colorize ? `stroke: #cc0000;` : "stroke: var(--Signal-Grey)")}
    }
    rect {
      fill: #cc000015;
    }
  }
  .extra-info-axis {
    text {
    font-size: 9px;
    fill: var(--Text-Secondary);
    }
  }
  .boxplot {
    .minMax {
      stroke: var(--Signal-Grey);
      fill: transparent;
       &.max {
           ${(props) => (props.colorize ? `stroke: #3d85c6` : `stroke:var(--Signal-Grey)`)};
        }
        &.min {
           ${(props) => (props.colorize ? `stroke: #cc0000` : `stroke:var(--Signal-Grey)`)};
        }
    }
    line {
      stroke: var(--Text-Secondary);
        &.percentile10 {
          ${(props) => (props.colorize ? `stroke: #cc0000;` : `stroke:var(--Signal-Grey);`)};
        }
        &.percentile90 {
          ${(props) => (props.colorize ? `stroke: #3d85c6;` : `stroke:var(--Signal-Grey);`)};
        }
    }
    .rect-path {
      &.rect-max {
        path {
          ${(props) => (props.colorize ? `fill: #6aa84f;` : "")};
        }
      }
      &.rect-min {
        path {
          ${(props) => (props.colorize ? `fill: #e69138;` : "")};
        }
      }
      path {
        fill: var(--Box-Plot-Background);
        stroke: var(--Signal-Grey);
        stroke-width: 1px;
      }   
    }
    line.vert-max {
      ${(props) => (props.colorize ? `stroke: #3d85c6;` : "")}
    }
    line.vert-min {
      ${(props) => (props.colorize ? `stroke: #cc0000;` : "")}
    }
    rect {
      fill: var(--Background-Block);
      stroke: var(--Signal-Grey);
    }
    line {
      stroke: var(--Signal-Grey);
    }
    &.inverted {
      .minMax {
        &.max {
            ${(props) => (props.colorize ? `stroke: #cc0000;` : `stroke:var(--Signal-Grey);`)};
          }
          &.min {
            ${(props) => (props.colorize ? `stroke: #3d85c6;` : `stroke:var(--Signal-Grey);`)};
          }
      }
      .rect-path {
        &.rect-max {
          path {
            ${(props) => (props.colorize ? `fill: #e69138;` : "")}
          }
        }
        &.rect-min {
          path {
            ${(props) => (props.colorize ? `fill: #6aa84f;` : "")}
          }
        }  
      }
      line.percentile90{
        ${(props) => (props.colorize ? `stroke: #cc0000;` : "")}
      }
      line.percentile10{
        ${(props) => (props.colorize ? `stroke: #3d85c6;` : "")}
      }
      line.vert-max {
        ${(props) => (props.colorize ? `stroke: #cc0000;` : "")}
      }
      line.vert-min {
        ${(props) => (props.colorize ? `stroke: #3d85c6;` : "")}
      }
    }
  }
`;

const CenteredMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 100%;
  min-height: ${(props) => props.minHeight}px;
  width: ${(props) => (props.width ? props.width + "px" : "auto")};
  color: var(--Text-Primary);
`;

export const NoEnoughtData = ({ minHeight, width }) => (
  <CenteredMessage minHeight={minHeight} width={width}>
    Not enough data.
  </CenteredMessage>
);

const BoxPlot = ({
  data,
  width,
  height,
  margin,
  units,
  colorize,
  threshold,
  inverted
}) => {
  const boundsWidth = width - margin.right - margin.left;
  const boundsHeight = height - margin.top - margin.bottom;
  const [plot, setPlot] = useState({ x: 0, y: 0 });
  const plotRef = useRef(null);

  useEffect(() => {
    if (plotRef.current) {
      const rect = plotRef.current.getBoundingClientRect();
      setPlot({x: rect.left, y: rect.top});
    }
  }, [width, height]);

  const { chartMin, chartMax, validValues } = useMemo(() => {
    const validValues = data.reduce((acc, value) => {
      if (value !== "n/a" && value !== null) {
        acc.push(parseFloat(value));
      }
      return acc;
    }, []);

    const [chartMin, chartMax] = d3.extent(validValues);
    return { chartMin, chartMax, validValues };
  }, [data]);

  const yScale = d3
    .scaleLinear()
    .domain([chartMin, chartMax])
    .range([boundsHeight - GAP, GAP]);

  if (validValues.length < 2) {
    return (
      <WrapperPlot colorize={colorize}>
        <NoEnoughtData minHeight={boundsHeight} />
      </WrapperPlot>
    );
  }

  const quantileData = getQuantileStats(validValues);
  const { min, q1, median, q3, max, p10, p90 } = quantileData;
  const boxPlotClass = inverted ? "boxplot inverted" : "boxplot";

  return (
    <>
      <WrapperPlot
        ref={plotRef}
        colorize={colorize}
      >
        <svg width={width} height={height}>
          <g
            width={boundsWidth}
            height={boundsHeight}
            transform={`translate(${[margin.left, margin.top].join(",")})`}
          >
            <AxisLeft
              yScale={yScale}
              pixelsPerTick={30}
              height={boundsHeight - 2 * GAP}
              unit={units}
            />
            <g
              transform={`translate(${(boundsWidth * 0.30) / 2},0)`}
              className={boxPlotClass}
            >
              <VerticalBox
                width={boundsWidth * 0.35}
                q1={yScale(q1)}
                median={yScale(median)}
                q3={yScale(q3)}
                min={yScale(min)}
                max={yScale(max)}
                p10={yScale(p10)}
                p90={yScale(p90)}
              />
            </g>
            <g transform={`translate(0,${EXTRA_AXIS_Y_OFFSET})`} className= "extra-info-axis">
              <ExtraInfoAxis quantileData={quantileData} width={boundsWidth} height={boundsHeight} />
            </g>
            {threshold && threshold < chartMax ? (
              <g className="warning">
                {yScale(threshold) <= boundsHeight && <line
                  x1={0}
                  x2={boundsWidth}
                  y1={yScale(threshold)}
                  y2={yScale(threshold)}
                />}
                {!colorize ? (
                  <rect
                    x={0}
                    y={0}
                    width={boundsWidth}
                    height={Math.min(yScale(threshold) - yScale(chartMax) + GAP, boundsHeight)}
                  />
                ) : null}
              </g>
            ) : null}
            <rect
              className="background"
              width={boundsWidth}
              height={boundsHeight}
              rx="5"
              ry="5"
            />
          </g>
        </svg>
      </WrapperPlot>
    </>
  );
};

export default BoxPlot;

const TICK_LENGTH = 6;

const AxisLeft = ({ yScale, pixelsPerTick, height, unit }) => {
  const range = yScale.range();

  const ticks = useMemo(() => {
    const height = range[0] - range[1];
    const numberOfTicksTarget = Math.floor(height / pixelsPerTick);

    return yScale.ticks(numberOfTicksTarget).map((value) => ({
      value,
      yOffset: yScale(value),
    }));
  }, [yScale]);

  return (
    <g className="axis">
      {ticks.map(({ value, yOffset }) => {
        if (yOffset < 5 || yOffset > height - 5) {
          return null;
        }
        return (
          <g key={value} transform={`translate(0, ${yOffset})`}>
            <line x2={-TICK_LENGTH} />
            <text
              key={value}
              style={{
                textAnchor: "end",
                transform: "translateX(-12px) translateY(4px)",
              }}
            >
              {value}
            </text>
          </g>
        );
      })}
      <text
        x={0}
        y={-3}
        className="unit-axis"
        style={{
          textAnchor: "middle",
          rotate: "-90deg",
          transform: `translateX(-${height / 2}px) translateY(15px)`,
        }}
      >
        {unit}
      </text>
    </g>
  );
};

const gap = 5;

export const getMetrics = (quantileData) => {
  const {max, p90, q3:p75, q1:p25, p10, min, median} = quantileData;
  return [
  { key: "max", label: "MAX", value: max },
  { key: "p90", label: "90%", value: p90 },
  { key: "p75", label: "75%", value: p75 },
  { key: "median", label: "MED", value: median },
  { key: "p25", label: "25%", value: p25 },
  { key: "p10", label: "10%", value: p10 },
  { key: "min", label: "MIN", value: min },
]};

const ExtraInfoAxis = ({ quantileData, width, height }) => {
  const offsetLabels = width - 30;
  const offsetValues = width - 3;

  return (
  <>
   {getMetrics(quantileData).map(({ key, label, value }, index) => {
        if (value === undefined) return null;
        const formattedValue =
        key === "min" || key === "max" || key === "median"
          ? decimalFormat(value)
          : `<=${decimalFormat(value)}`;
        return (
          <React.Fragment key={key}>
            <text x={offsetLabels} y={(height/7)*(index+1)} textAnchor="end">
              {label}
            </text>
            <text x={offsetValues} y={(height/7)*(index+1)} textAnchor="end">
              {formattedValue}
            </text>
          </React.Fragment>
        );
      })}
   </>
   )
}
  
const RADIUS = 5;

export const VerticalBox = ({ min, q1, median, q3, max, width, p10, p90, }) => {
    const radiusMax = Math.abs(median - q3) > RADIUS ? RADIUS : Math.abs(median - q3);
    const radiusMin = Math.abs(q1 - median) > RADIUS ? RADIUS : Math.abs(q1 - median);
    const maxPath = createRectanglePath({
        width,
        height: median - q3,
        bottomLeftRadius: 0,
        bottomRightRadius: 0,
        topLeftRadius: radiusMax,
        topRightRadius: radiusMax
      });
      const minPath = createRectanglePath({
        width,
        height: q1 -median,
        bottomLeftRadius: radiusMin,
        bottomRightRadius: radiusMin,
        topLeftRadius: 0,
        topRightRadius: 0
      });
  return (
    <>
      <circle cx={width / 2} cy={max} r="3" className="minMax max" />
      <circle cx={width / 2} cy={min} r="3" className="minMax min" />
      <line x1={0} x2={width} y1={p10} y2={p10} className="percentile10" />
      <line
        x1={width / 2}
        x2={width / 2}
        y1={min}
        y2={p10}
        className="percentile10"
        strokeDasharray="0 3 0"
      />
      <line
        x1={width / 2}
        x2={width / 2}
        y1={p10}
        y2={q1}
        className="percentile10"
      />
      <line
        x1={width / 2}
        x2={width / 2}
        y1={p90}
        y2={max}
        className="percentile90"
        strokeDasharray="0 3 0"
      />
      <line
        x1={width / 2}
        x2={width / 2}
        y1={p90}
        y2={q3}
        className="percentile90"
      />
      <g className="rect-path rect-max"  transform={`translate(${[0, q3].join(",")})`}>
        <path d={maxPath} />
      </g>
      <g className="rect-path rect-min"  transform={`translate(${[0, median].join(",")})`}>
        <path d={minPath} />
      </g>
      <line x1={0} x2={width} y1={p90} y2={p90} className="percentile90" />
    </>
  );
};

const decimalFormat = d3.format(".1f");